// AuthContext.js
import axios from 'axios';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'; // ADD useMemo
import api from '../api'; // Your updated api.js
import {
    clearStoredTokens,
    getStoredAccessToken,
    getStoredRefreshToken,
    setStoredTokens,
} from '../services/tokenStorage';

const AuthContext = createContext(null); // It's good practice to initialize with null or a default object

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUser = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('auth/user/');
            setUser(res.data);
        } catch (err) {
            console.error('Failed to fetch user (initial load/refresh attempt):', err.response?.data || err.message);
            setUser(null);
            clearStoredTokens();
        } finally {
            setLoading(false);
        }
    }, []); // fetchUser itself has no dependencies, so it's created once

    const mergeUserSubscription = useCallback((subscriptionPatch) => {
        if (!subscriptionPatch) {
            return;
        }

        setUser(prevUser => {
            if (!prevUser) {
                return prevUser;
            }

            return {
                ...prevUser,
                entitlements: Array.isArray(subscriptionPatch.entitlements)
                    ? subscriptionPatch.entitlements
                    : prevUser.entitlements,
                profile: {
                    ...prevUser.profile,
                    ...(subscriptionPatch.subscription_status ? { subscription_status: subscriptionPatch.subscription_status } : {}),
                    ...(subscriptionPatch.subscription_tier ? { subscription_tier: subscriptionPatch.subscription_tier } : {}),
                    ...(subscriptionPatch.subscription_expiry_date ? { subscription_expiry_date: subscriptionPatch.subscription_expiry_date } : {}),
                    ...(Array.isArray(subscriptionPatch.entitlements) ? { entitlements: subscriptionPatch.entitlements } : {}),
                },
            };
        });
    }, []);

    // Fetches the authoritative subscription state from the dedicated endpoint.
    // Called after app init and on visibility change so gating decisions are
    // always based on server state, not potentially-stale JWT claims.
    const fetchSubscriptionStatus = useCallback(async () => {
        try {
            const res = await api.get('user/subscription-status/');
            mergeUserSubscription(res.data);
        } catch (err) {
            console.warn('Failed to sync subscription status:', err.response?.data || err.message);
        }
    }, [mergeUserSubscription]);

    useEffect(() => {
        const accessToken = getStoredAccessToken();
        const refreshToken = getStoredRefreshToken();

        if (accessToken || refreshToken) {
            // fetchUser establishes the full identity; fetchSubscriptionStatus then
            // hydrates subscription state from the dedicated source-of-truth endpoint.
            fetchUser().then(() => {
                if (getStoredAccessToken() || getStoredRefreshToken()) {
                    fetchSubscriptionStatus();
                }
            });
        } else {
            setLoading(false);
            setUser(null);
        }
    }, [fetchUser, fetchSubscriptionStatus]);

    // Re-sync subscription status whenever the user returns to this tab.
    // This catches backend-side changes (cancellation, failed payment, webhook)
    // that happened while the user was on a different tab or the Stripe portal.
    // Uses the lightweight subscription-status endpoint rather than a full user re-fetch.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const token =
                    getStoredAccessToken() ||
                    getStoredRefreshToken();
                if (token) fetchSubscriptionStatus();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchSubscriptionStatus]);

    const login = useCallback(async (email, password) => { // Wrap login in useCallback
        try {
            const res = await api.post('token/', { email, password });
            setStoredTokens({ access: res.data.access, refresh: res.data.refresh });

            await fetchUser(); // This will use the newly stored tokens

            return res;
        } catch (error) {
            console.error('Login failed:', error.response?.data || error.message);
            clearStoredTokens();
            setUser(null);
            throw error;
        }
    }, [fetchUser]); // login depends on fetchUser

    const logout = useCallback(async () => { // Wrap logout in useCallback
        clearStoredTokens();
        setUser(null);
        // Optional: Call backend for token blacklisting if implemented
    }, []);

    const refreshAuthSession = useCallback(async () => {
        const refreshToken = getStoredRefreshToken();

        if (!refreshToken) {
            throw new Error('Missing refresh token.');
        }

        const refreshUrl = `${process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '')}/api/token/refresh/`;
        const response = await axios.post(refreshUrl, { refresh: refreshToken });

        setStoredTokens({ access: response.data.access, refresh: response.data.refresh });
        return response.data;
    }, []);

    const register = useCallback(async (data) => { // Wrap register in useCallback
        try {
            // 1. First, send the registration request to create the user account
            await api.post('auth/registration/', data);

            // 2. If the registration is successful, immediately perform a login
            await login(data.email, data.password1); // Assuming data.password1 is the correct password field

            return { success: true, message: 'Registration successful and logged in!' };

        } catch (error) {
            console.error('Registration failed:', error.response?.data || error.message);
            clearStoredTokens();
            setUser(null);
            throw error;
        }
    }, [login]); // register depends on login

    // Post-purchase account creation: registration itself returns JWTs
    // (unlike the plain `register` flow above), so we consume them directly
    // instead of performing a second login round-trip.
    const registerWithPurchaseClaim = useCallback(async (data) => {
        const res = await api.post('auth/registration/', data);

        if (res.data?.access && res.data?.refresh) {
            setStoredTokens({ access: res.data.access, refresh: res.data.refresh });
            await fetchUser();
            await fetchSubscriptionStatus();
        } else {
            // Backend didn't return tokens for some reason — fall back to a
            // normal login so the user still ends up authenticated.
            await login(data.email, data.password1);
        }

        return res.data;
    }, [fetchUser, fetchSubscriptionStatus, login]);

    // Links an already-claimed post-purchase subscription to the now
    // logged-in user. Requires the caller to already be authenticated.
    const claimPurchaseSubscription = useCallback(async (claimToken) => {
        const res = await api.post('auth/post-purchase/claim/', { claim_token: claimToken });
        mergeUserSubscription({
            subscription_status: res.data?.subscription_status,
            subscription_tier: res.data?.subscription_tier,
            subscription_expiry_date: res.data?.subscription_expiry_date,
        });
        return res.data;
    }, [mergeUserSubscription]);

    const requestPasswordReset = useCallback((email) => // Wrap in useCallback
        api.post('auth/password/reset/', { email })
    , []);

    const confirmPasswordReset = useCallback(async (uid, token, new_password1, new_password2) => {
        // ===== COMPREHENSIVE PAYLOAD DEBUGGING =====
        console.group('🔍 PASSWORD RESET DEBUG - Detailed Payload Analysis');
        
        // 1. Check for undefined/null values
        console.log('1️⃣ Value Existence Check:', {
            uid: uid === undefined ? '❌ UNDEFINED' : uid === null ? '❌ NULL' : '✅ EXISTS',
            token: token === undefined ? '❌ UNDEFINED' : token === null ? '❌ NULL' : '✅ EXISTS',
            new_password1: new_password1 === undefined ? '❌ UNDEFINED' : new_password1 === null ? '❌ NULL' : '✅ EXISTS',
            new_password2: new_password2 === undefined ? '❌ UNDEFINED' : new_password2 === null ? '❌ NULL' : '✅ EXISTS',
        });
        
        // 2. Check data types
        console.log('2️⃣ Data Type Check:', {
            uid: typeof uid,
            token: typeof token,
            new_password1: typeof new_password1,
            new_password2: typeof new_password2,
        });
        
        // 3. Check lengths and content
        console.log('3️⃣ Length & Content Check:', {
            uid: {
                length: uid?.length,
                value: uid,
                hasWhitespace: uid !== uid?.trim(),
                firstChar: uid?.[0],
                lastChar: uid?.[uid?.length - 1],
            },
            token: {
                length: token?.length,
                value: token,
                hasWhitespace: token !== token?.trim(),
                preview: token?.substring(0, 20) + '...',
                firstChar: token?.[0],
                lastChar: token?.[token?.length - 1],
            },
            passwords: {
                length1: new_password1?.length,
                length2: new_password2?.length,
                match: new_password1 === new_password2,
            }
        });
        
        // 4. Check for special characters or encoding issues
        console.log('4️⃣ Character Encoding Check:', {
            uid: {
                containsSpecialChars: /[^a-zA-Z0-9_-]/.test(uid),
                charCodes: uid?.split('').map(c => c.charCodeAt(0)),
            },
            token: {
                containsSpecialChars: /[^a-zA-Z0-9_-]/.test(token),
                charCodes: token?.split('').slice(0, 20).map(c => c.charCodeAt(0)),
            }
        });
        
        // 5. Create the actual payload
        const requestPayload = {
            uid: uid,
            token: token,
            new_password1: new_password1,
            new_password2: new_password2,
        };
        
        console.log('5️⃣ Request Payload:', {
            payloadKeys: Object.keys(requestPayload),
            payloadStringified: JSON.stringify(requestPayload, null, 2),
            payloadSize: JSON.stringify(requestPayload).length + ' bytes',
        });
        
        // 6. API URL check
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/auth/password/reset/confirm/`;
        console.log('6️⃣ API Configuration:', {
            baseURL: process.env.REACT_APP_API_BASE_URL,
            fullURL: apiUrl,
            envVarExists: !!process.env.REACT_APP_API_BASE_URL,
        });
        
        console.groupEnd();
        
        try {
            console.log('📤 Sending request to backend...');
            const response = await api.post('auth/password/reset/confirm/', requestPayload);
            console.log('✅ Password Reset Success:', response.data);
            return response;
        } catch (error) {
            console.group('❌ PASSWORD RESET ERROR - Detailed Analysis');
            
            console.log('Error Type:', error.name);
            console.log('Error Message:', error.message);
            
            if (error.response) {
                console.log('Response Status:', error.response.status);
                console.log('Response Status Text:', error.response.statusText);
                console.log('Response Headers:', error.response.headers);
                console.log('Response Data:', error.response.data);
                
                // Detailed 400 error analysis
                if (error.response.status === 400) {
                    console.group('🔎 400 Bad Request - Root Cause Analysis');
                    
                    const errorData = error.response.data;
                    
                    if (errorData.uid) {
                        console.error('🚨 UID ERROR:', errorData.uid);
                        console.log('Sent UID:', uid);
                        console.log('UID Issues:', {
                            isEmpty: !uid || uid.length === 0,
                            hasWhitespace: uid !== uid?.trim(),
                            invalidChars: /[^a-zA-Z0-9_-]/.test(uid),
                            tooShort: uid?.length < 1,
                            tooLong: uid?.length > 100,
                        });
                        console.log('💡 Possible causes:');
                        console.log('  - UID is malformed or corrupted');
                        console.log('  - UID has already been used');
                        console.log('  - UID has expired');
                        console.log('  - User ID encoded in UID does not exist');
                    }
                    
                    if (errorData.token) {
                        console.error('🚨 TOKEN ERROR:', errorData.token);
                        console.log('Sent Token:', token?.substring(0, 20) + '...');
                        console.log('Token Issues:', {
                            isEmpty: !token || token.length === 0,
                            hasWhitespace: token !== token?.trim(),
                            invalidFormat: !/^[a-z0-9]+-[a-z0-9]+$/.test(token),
                            tooShort: token?.length < 10,
                        });
                        console.log('💡 Possible causes:');
                        console.log('  - Token has expired');
                        console.log('  - Token has already been used');
                        console.log('  - Token format is invalid');
                    }
                    
                    if (errorData.new_password1) {
                        console.error('🚨 PASSWORD1 ERROR:', errorData.new_password1);
                        console.log('💡 Backend rejected the password. Check password requirements.');
                    }
                    
                    if (errorData.new_password2) {
                        console.error('🚨 PASSWORD2 ERROR:', errorData.new_password2);
                    }
                    
                    if (errorData.non_field_errors) {
                        console.error('🚨 GENERAL ERROR:', errorData.non_field_errors);
                    }
                    
                    if (errorData.detail) {
                        console.error('🚨 DETAIL:', errorData.detail);
                    }
                    
                    console.groupEnd();
                }
            } else if (error.request) {
                console.error('No response received from server');
                console.log('Request details:', error.request);
            } else {
                console.error('Error setting up request:', error.message);
            }
            
            console.groupEnd();
            throw error;
        }
    }, []);

    // Memoize the context value itself
    const authContextValue = useMemo(() => ({
        user,
        loading,
        login,
        logout,
        register,
        registerWithPurchaseClaim,
        claimPurchaseSubscription,
        fetchUser,
        fetchSubscriptionStatus,
        refreshAuthSession,
        mergeUserSubscription,
        requestPasswordReset,
        confirmPasswordReset,
    }), [user, loading, login, logout, register, registerWithPurchaseClaim, claimPurchaseSubscription, fetchUser, fetchSubscriptionStatus, refreshAuthSession, mergeUserSubscription, requestPasswordReset, confirmPasswordReset]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === null) {
        // This check is important for scenarios where useAuth is called outside of AuthProvider
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}